import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import TipCard from '../components/TipCard';
import { Button } from '../components/ui/button';
import { Lightbulb, Loader2, Sparkles, ShoppingCart, Package, ChefHat, Wallet, Plus } from 'lucide-react';

export const TipsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    
    const [tips, setTips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState(searchParams.get('category') || 'all');

    const categories = [
        { value: 'all', label: 'Toutes', icon: Sparkles, color: 'bg-gray-500' },
        { value: 'shopping', label: 'Courses', icon: ShoppingCart, color: 'bg-green-500' },
        { value: 'storage', label: 'Conservation', icon: Package, color: 'bg-cyan-500' },
        { value: 'cooking', label: 'Cuisine', icon: ChefHat, color: 'bg-orange-500' },
        { value: 'budget', label: 'Budget', icon: Wallet, color: 'bg-purple-500' }
    ];

    useEffect(() => {
        loadTips();
    }, [searchParams]);

    const loadTips = async () => {
        setLoading(true);
        try {
            const params = {};
            const cat = searchParams.get('category');
            if (cat && cat !== 'all') {
                params.category = cat;
            }
            
            const response = await api.getTips(params);
            setTips(response.data);
        } catch (error) {
            console.error('Error loading tips:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCategoryChange = (value) => {
        setCategory(value);
        if (value === 'all') {
            setSearchParams({});
        } else {
            setSearchParams({ category: value });
        }
    };

    return (
        <div className="min-h-screen py-8 md:py-12 relative overflow-hidden">
            {/* Decorative blobs */}
            <div className="blob-orange top-20 -right-40 opacity-20" />
            <div className="blob-purple bottom-40 -left-40 opacity-20" />
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="w-20 h-20 bg-orange-500 rounded-3xl flex items-center justify-center mx-auto mb-6 border-2 border-foreground shadow-pop rotate-3 hover:rotate-0 transition-transform">
                        <Lightbulb className="w-10 h-10 text-white" />
                    </div>
                    <h1 className="font-heading text-5xl md:text-6xl font-black text-foreground mb-4">
                        Astuces pour <span className="text-gradient-cameleon">économiser</span>
                    </h1>
                    <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                        Des conseils pratiques et malins pour réduire vos dépenses alimentaires 
                        sans sacrifier la qualité de vos repas
                    </p>
                    
                    {/* Submit Button */}
                    <Button
                        onClick={() => user ? navigate('/tips/submit') : navigate('/login')}
                        className="mt-6 btn-pop bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-full px-6 py-5 font-bold"
                        data-testid="submit-tip-btn"
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        Proposer une astuce
                    </Button>
                </div>

                {/* Category Pills */}
                <div className="flex flex-wrap justify-center gap-3 mb-12">
                    {categories.map(cat => {
                        const IconComponent = cat.icon;
                        return (
                            <button
                                key={cat.value}
                                onClick={() => handleCategoryChange(cat.value)}
                                className={`flex items-center gap-2 px-5 py-3 rounded-full font-heading font-bold transition-all border-2 ${
                                    category === cat.value 
                                        ? `${cat.color} text-white border-foreground shadow-pop` 
                                        : 'bg-white text-foreground border-foreground hover:-translate-y-1 hover:shadow-pop'
                                }`}
                                data-testid={`tip-category-${cat.value}`}
                            >
                                <IconComponent className="w-5 h-5" />
                                {cat.label}
                            </button>
                        );
                    })}
                </div>

                {/* Tips Grid */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="text-center">
                            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                            <p className="text-muted-foreground font-medium">Chargement des astuces...</p>
                        </div>
                    </div>
                ) : tips.length === 0 ? (
                    <div className="text-center py-20">
                        <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-foreground shadow-pop">
                            <Lightbulb className="w-12 h-12 text-muted-foreground" />
                        </div>
                        <h3 className="font-heading font-bold text-2xl text-foreground mb-2">
                            Aucune astuce trouvée
                        </h3>
                        <p className="text-muted-foreground mb-6">
                            Essayez une autre catégorie
                        </p>
                        <Button
                            onClick={() => handleCategoryChange('all')}
                            className="btn-pop bg-white text-foreground"
                        >
                            Voir toutes les astuces
                        </Button>
                    </div>
                ) : (
                    <>
                        <p className="text-center text-muted-foreground mb-8 font-medium">
                            <span className="text-foreground font-bold">{tips.length}</span> astuce{tips.length > 1 ? 's' : ''} trouvée{tips.length > 1 ? 's' : ''}
                        </p>
                        
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                            {tips.map((tip, index) => (
                                <div key={tip.id} className={`animate-fade-in stagger-${(index % 5) + 1}`}>
                                    <TipCard tip={tip} />
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default TipsPage;
